import { ArrowBack, Close, HorizontalRule, Image as ImageIcon, Notes, SmartButton, TextFields, ViewColumn } from '@mui/icons-material';
import { AppBar, Box, Button, Dialog, MenuItem, Stack, TextField, Toolbar, Typography, alpha } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { BlockCanvas } from '../builder/blockCanvas';
import {
  DropTarget,
  duplicateBlockById,
  findBlockById,
  insertNewBlockAtTarget,
  moveBlockById,
  removeBlockById,
  reorderBlockToTarget,
  updateBlockById,
} from '../builder/blockOps';
import { MIME_NEW } from '../builder/dnd';
import { BlockEditor, blockTypeLabelKey } from '../builder/editors';
import { parseHtml } from '../builder/parse';
import { PersistentTinyMce, PersistentTinyMceHandle } from '../builder/persistentTinyMce';
import { renderBlocks } from '../builder/serialize';
import {
  AVAILABLE_FONT_STACKS,
  Block,
  BlockId,
  BlockType,
  buildGoogleFontsUrl,
  DEFAULT_DOC_SETTINGS,
  EmailDocSettings,
  findGoogleFamily,
} from '../builder/types';

export type EmailBuilderMode = 'direct' | 'design';

type EmailBuilderModalProps = {
  open: boolean;
  onClose: () => void;
  mode: EmailBuilderMode;
};

const PALETTE_ITEMS: { key: BlockType; labelKey: string; icon: React.ReactNode }[] = [
  { key: 'heading', labelKey: 'groupMessage:builder.palette.heading', icon: <TextFields /> },
  { key: 'text', labelKey: 'groupMessage:builder.palette.text', icon: <Notes /> },
  { key: 'image', labelKey: 'groupMessage:builder.palette.image', icon: <ImageIcon /> },
  { key: 'twoColumns', labelKey: 'groupMessage:builder.palette.twoColumns', icon: <ViewColumn /> },
  { key: 'button', labelKey: 'groupMessage:builder.palette.button', icon: <SmartButton /> },
  { key: 'divider', labelKey: 'groupMessage:builder.palette.divider', icon: <HorizontalRule /> },
];

export const EmailBuilderModal = (props: EmailBuilderModalProps) => {
  const { open, onClose, mode } = props;
  const { t } = useTranslation(['groupMessage', 'common']);
  const { getValues, setValue } = useFormContext();

  // Block-state lever bara medan modal:en är öppen. Vid öppning parsas body till blocks,
  // vid stängning serialiseras tillbaka och skrivs till body-fältet.
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<BlockId | null>(null);
  // Spårar att en användare just lade in ett nytt block — sidopanel-editorn
  // pulserar då i ~1.5s så att användaren förstår var hen ska redigera.
  const [recentlyAddedHighlight, setRecentlyAddedHighlight] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerEditorHighlight = useCallback(() => {
    setRecentlyAddedHighlight(true);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => setRecentlyAddedHighlight(false), 2500);
  }, []);
  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    },
    [],
  );
  // Dokument-nivå inställningar (body-bg, content-bg). Speglar envelope-mönstret
  // i system-mallarna och styr både hur canvas ritas och hur slut-HTML serialiseras.
  const [docSettings, setDocSettings] = useState<EmailDocSettings>(DEFAULT_DOC_SETTINGS);

  // PersistentTinyMce-instansen och vilket TextBlock som för närvarande har
  // sitt innehåll laddat i editorn. Används för att skriva tillbaka editor-content
  // när användaren växlar mellan textblock.
  const editorRef = useRef<PersistentTinyMceHandle | null>(null);
  const activeTextBlockIdRef = useRef<BlockId | null>(null);
  // Flippas till true när TinyMCE har initierats — triggar effekten att ladda
  // innehåll om ett textblock blev valt innan editorn var redo.
  const [editorReady, setEditorReady] = useState(false);
  // Senaste blocks-arrayen i en ref så att onEditorChange (som har stale closures)
  // alltid kan läsa det aktuella state-värdet utan att lägga blocks i useEffect-deps.
  const blocksRef = useRef<Block[]>([]);
  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    if (open && mode === 'design') {
      const html = (getValues('body') as string) || '';
      const parsed = parseHtml(html);
      setBlocks(parsed.blocks);
      setDocSettings(parsed.settings);
      setSelectedBlockId(null);
      activeTextBlockIdRef.current = null;
    }
    if (!open) {
      // PersistentTinyMce unmountas när Dialog stänger — släpp ready-flaggan så
      // att vi inte refererar en gammal editor-instans vid nästa öppning.
      setEditorReady(false);
    }
  }, [open, mode, getValues]);

  // Vald block (om någon) — söker rekursivt så att även nestade block i twoColumns hittas.
  const selectedBlock = selectedBlockId ? findBlockById(blocks, selectedBlockId) : null;

  // Skriv tillbaka blocks → HTML innan vi stänger.
  const handleClose = () => {
    if (mode === 'design') {
      // Säkerställ att senaste TinyMCE-innehåll committeras till blocks innan serialize.
      let finalBlocks = blocksRef.current;
      if (activeTextBlockIdRef.current && editorRef.current) {
        const latest = editorRef.current.getContent();
        finalBlocks = updateBlockById(finalBlocks, activeTextBlockIdRef.current, { html: latest } as Partial<Block>);
      }
      if (finalBlocks.length > 0) {
        const html = renderBlocks(finalBlocks, docSettings);
        setValue('body', html, { shouldDirty: true });
      }
    }
    onClose();
  };

  // Refen håller en stable referens till handleClose så popstate-effekten kan
  // anropa "senaste versionen" utan att registreras om på varje render.
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  // Injicera Google Fonts via <link> i document.head så att canvas-preview
  // visar de valda fonterna. Stylesheeten byts ut när användaren ändrar
  // typsnitt — så användaren ser fonten omedelbart i editorn.
  useEffect(() => {
    if (!open || mode !== 'design') return undefined;
    const url = buildGoogleFontsUrl([findGoogleFamily(docSettings.headingFontFamily), findGoogleFamily(docSettings.bodyFontFamily)]);
    if (!url) return undefined;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.dataset.fsDesignFonts = '1';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [open, mode, docSettings.headingFontFamily, docSettings.bodyFontFamily]);

  // Skydda mot browser-back: när modalen öppnas pushar vi en history-entry
  // som "fångar" första bakåt-tryckningen och stänger modalen istället för att
  // navigera bort från sidan. handleClose serialiserar blocks → body innan
  // den stänger så användarens ändringar inte tappas.
  useEffect(() => {
    if (!open || mode !== 'design') return undefined;
    window.history.pushState({ designModalOpen: true }, '');
    const handlePop = () => {
      handleCloseRef.current();
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
    };
  }, [open, mode]);

  // Klick-att-lägga-till från palett — lägger alltid sist på top-level. För
  // mer kontroll använder användaren drag-and-drop med drop-zonerna.
  const handleAddBlock = (type: BlockType) => {
    let createdId: BlockId | null = null;
    setBlocks((prev) => {
      const { blocks: next, newId } = insertNewBlockAtTarget(prev, type, { kind: 'top', index: prev.length });
      createdId = newId;
      return next;
    });
    if (createdId) {
      setSelectedBlockId(createdId);
      triggerEditorHighlight();
    }
  };

  // Lägger in nytt block vid given drop-target (top-level eller column).
  const handleInsertBlock = useCallback(
    (type: BlockType, target: DropTarget) => {
      let createdId: BlockId | null = null;
      setBlocks((prev) => {
        const { blocks: next, newId } = insertNewBlockAtTarget(prev, type, target);
        createdId = newId;
        return next;
      });
      if (createdId) {
        setSelectedBlockId(createdId);
        triggerEditorHighlight();
      }
    },
    [triggerEditorHighlight],
  );

  // Flyttar ett befintligt block till nytt target. Justerar index om vi flyttar
  // INOM samma förälder och målet ligger efter ursprungspositionen — annars
  // skulle "ta bort sen sätta in" hamna ett steg fel.
  const handleReorderBlock = useCallback((id: BlockId, target: DropTarget) => {
    setBlocks((prev) => {
      // Hitta nuvarande position så vi kan justera vid intra-parent move.
      const findIdx = (list: Block[]): number => list.findIndex((b) => b.id === id);
      let adjusted: DropTarget = target;
      if (target.kind === 'top') {
        const i = findIdx(prev);
        if (i >= 0 && i < target.index) adjusted = { ...target, index: target.index - 1 };
      } else {
        // column-target: kolla om blocket redan ligger i samma kolumn
        const parent = prev.find((b) => b.id === target.parentId);
        if (parent && parent.type === 'twoColumns') {
          const col = target.column === 'left' ? parent.left : parent.right;
          const i = findIdx(col);
          if (i >= 0 && i < target.index) adjusted = { ...target, index: target.index - 1 };
        }
      }
      return reorderBlockToTarget(prev, id, adjusted);
    });
  }, []);

  // Förflyttar block ett steg upp eller ner via toolbar-knapparna. Helper
  // hanterar både top-level och kolumn-nivå transparent.
  const handleMoveBlock = useCallback((id: BlockId, direction: 'up' | 'down') => {
    setBlocks((prev) => moveBlockById(prev, id, direction));
  }, []);

  // Duplicerar ett block direkt efter originalet inom samma förälder.
  const handleDuplicateBlock = useCallback((id: BlockId) => {
    let newId: BlockId | null = null;
    setBlocks((prev) => {
      const res = duplicateBlockById(prev, id);
      newId = res.newId;
      return res.blocks;
    });
    if (newId) setSelectedBlockId(newId);
  }, []);

  // Raderar block. Om det var ett aktivt textblock måste activeTextBlockIdRef
  // nollställas så att nästa effekt inte försöker spara editor-content till
  // ett raderat block. Helper hanterar nestade block.
  const handleDeleteBlock = useCallback((id: BlockId) => {
    if (activeTextBlockIdRef.current === id) {
      activeTextBlockIdRef.current = null;
    }
    setBlocks((prev) => removeBlockById(prev, id).blocks);
    setSelectedBlockId((sel) => (sel === id ? null : sel));
  }, []);

  // Mergar in patch i valt block. Identiteten på syskon bevaras så React
  // inte unmountar i onödan; rekursivt via updateBlockById för nestade block.
  const updateSelectedBlock = useCallback(
    (patch: Partial<Block>) => {
      if (!selectedBlockId) return;
      setBlocks((prev) => updateBlockById(prev, selectedBlockId, patch));
    },
    [selectedBlockId],
  );

  // Hanterar växling mellan textblock: skriv tillbaka föregående blocks innehåll
  // och ladda nästas. När icke-text-block väljs eller selection rensas hålls
  // PersistentTinyMce mountad men display:none — så att vi slipper re-init.
  useEffect(() => {
    if (mode !== 'design') return;
    if (!editorReady || !editorRef.current) return;

    const prevId = activeTextBlockIdRef.current;
    const nextTextBlock = selectedBlock && selectedBlock.type === 'text' ? selectedBlock : null;

    // Skriv tillbaka föregående innehåll om det fanns ett aktivt textblock
    if (prevId && prevId !== nextTextBlock?.id) {
      const latest = editorRef.current.getContent();
      setBlocks((prevBlocks) => updateBlockById(prevBlocks, prevId, { html: latest } as Partial<Block>));
      activeTextBlockIdRef.current = null;
    }

    // Ladda in nytt innehåll om det är ett textblock som nu är aktivt
    if (nextTextBlock && nextTextBlock.id !== activeTextBlockIdRef.current) {
      editorRef.current.setContent(nextTextBlock.html ?? '');
      activeTextBlockIdRef.current = nextTextBlock.id;
    }
  }, [mode, selectedBlock, editorReady]);

  // onChange från TinyMCE — uppdaterar aktivt textblock kontinuerligt så canvas
  // speglar redigeringen i nästan realtid. Rekursivt så nestade textblock i
  // twoColumns också uppdateras.
  const handleEditorChange = useCallback((html: string) => {
    const id = activeTextBlockIdRef.current;
    if (!id) return;
    setBlocks((prev) => updateBlockById(prev, id, { html } as Partial<Block>));
  }, []);

  const isTextSelected = selectedBlock?.type === 'text';

  // disableEnforceFocus: TinyMCE:s dialoger (t.ex. Länk) renderas i en portal utanför
  // modalen – utan detta drar MUI:s focus-trap tillbaka fokus så URL-fältet inte går att skriva i
  return (
    <Dialog open={open} onClose={handleClose} fullScreen disableEnforceFocus>
      <AppBar
        position='relative'
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 0 rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
          zIndex: 2,
        }}
      >
        <Toolbar>
          <Typography variant='h6' sx={{ flex: 1, fontWeight: 700 }}>
            {mode === 'direct' ? t('groupMessage:builder.directTitle') : t('groupMessage:builder.title')}
          </Typography>
          <Button variant='outlined' color='primary' onClick={handleClose} startIcon={<Close />} sx={{ textTransform: 'none' }}>
            {t('groupMessage:builder.closeDesign')}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, bgcolor: 'grey.50' }}>
        {/* Vänsterpanel — palett + doc-settings + persistent TinyMCE (när textblock valt) + per-block editor. */}
        {mode === 'design' && (
          <Box
            sx={{
              width: 380,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
              zIndex: 1,
            }}
          >
            {selectedBlock && (
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button onClick={() => setSelectedBlockId(null)} startIcon={<ArrowBack />} size='small' sx={{ textTransform: 'none' }}>
                  {t('groupMessage:builder.backToPalette')}
                </Button>
              </Box>
            )}

            {/* PersistentTinyMce — alltid mountad i sidopanelen för persistence. Visas bara
                när ett textblock är valt. Display-toggle istället för unmount eftersom
                full re-init av TinyMCE är märkbart laggig. */}
            <Box
              sx={{
                display: isTextSelected ? 'block' : 'none',
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                position: 'relative',
                ...(isTextSelected &&
                  recentlyAddedHighlight && {
                    animation: 'fs-text-editor-pulse 2.4s ease-in-out',
                    '@keyframes fs-text-editor-pulse': {
                      '0%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                      '15%': { boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.55), 0 0 18px rgba(25, 118, 210, 0.55)' },
                      '40%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                      '60%': { boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.55), 0 0 18px rgba(25, 118, 210, 0.55)' },
                      '100%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                    },
                  }),
              }}
            >
              <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {t(blockTypeLabelKey('text'))}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {t('groupMessage:builder.textEditorHint')}
                </Typography>
              </Box>
              <PersistentTinyMce ref={editorRef} initialContent='' onChange={handleEditorChange} onReady={() => setEditorReady(true)} height={360} />
            </Box>

            {selectedBlock && !isTextSelected && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  position: 'relative',
                  // Pulserande box-shadow när användaren just lagt till ett block.
                  // Två puls-toppar över 2.4s ger tydlig visuell ankning utan att
                  // bli irriterande.
                  ...(recentlyAddedHighlight && {
                    animation: 'fs-editor-pulse 2.4s ease-in-out',
                    '@keyframes fs-editor-pulse': {
                      '0%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                      '15%': { boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.55), 0 0 18px rgba(25, 118, 210, 0.55)' },
                      '40%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                      '60%': { boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.55), 0 0 18px rgba(25, 118, 210, 0.55)' },
                      '100%': { boxShadow: 'inset 0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 0 rgba(25, 118, 210, 0)' },
                    },
                  }),
                }}
              >
                <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2 }}>
                  {t(blockTypeLabelKey(selectedBlock.type))}
                </Typography>
                <BlockEditor block={selectedBlock} onChange={updateSelectedBlock} />
              </Box>
            )}

            {!selectedBlock && (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                    {t('groupMessage:builder.paletteHeader')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {t('groupMessage:builder.paletteHint')}
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                  {PALETTE_ITEMS.map((item) => (
                    <Box
                      key={item.key}
                      role='button'
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(MIME_NEW, item.key);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => handleAddBlock(item.key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAddBlock(item.key);
                        }
                      }}
                      sx={{
                        py: 2,
                        px: 1,
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                        bgcolor: 'background.paper',
                        textAlign: 'center',
                        cursor: 'grab',
                        userSelect: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'primary.main',
                        transition: 'background-color 120ms ease, border-color 120ms ease',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          borderColor: 'primary.main',
                        },
                        '&:active': {
                          cursor: 'grabbing',
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {item.icon}
                      <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {t(item.labelKey)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5 }}>
                    {t('groupMessage:builder.docSettings.header')}
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Box
                        component='input'
                        type='color'
                        value={docSettings.bodyBgColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocSettings((s) => ({ ...s, bodyBgColor: e.target.value }))}
                        sx={{
                          width: 36,
                          height: 36,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <TextField
                        label={t('groupMessage:builder.docSettings.bodyBg') as string}
                        value={docSettings.bodyBgColor}
                        onChange={(e) => setDocSettings((s) => ({ ...s, bodyBgColor: e.target.value }))}
                        size='small'
                        sx={{ flex: 1, '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Box
                        component='input'
                        type='color'
                        value={docSettings.contentBgColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocSettings((s) => ({ ...s, contentBgColor: e.target.value }))}
                        sx={{
                          width: 36,
                          height: 36,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <TextField
                        label={t('groupMessage:builder.docSettings.contentBg') as string}
                        value={docSettings.contentBgColor}
                        onChange={(e) => setDocSettings((s) => ({ ...s, contentBgColor: e.target.value }))}
                        size='small'
                        sx={{ flex: 1, '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Stack>
                    <TextField
                      select
                      label={t('groupMessage:builder.docSettings.headingFont') as string}
                      value={docSettings.headingFontFamily}
                      onChange={(e) => setDocSettings((s) => ({ ...s, headingFontFamily: e.target.value }))}
                      size='small'
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                    >
                      {AVAILABLE_FONT_STACKS.map((f) => (
                        <MenuItem key={f.stack} value={f.stack} sx={{ fontFamily: f.stack }}>
                          {f.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label={t('groupMessage:builder.docSettings.bodyFont') as string}
                      value={docSettings.bodyFontFamily}
                      onChange={(e) => setDocSettings((s) => ({ ...s, bodyFontFamily: e.target.value }))}
                      size='small'
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                    >
                      {AVAILABLE_FONT_STACKS.map((f) => (
                        <MenuItem key={f.stack} value={f.stack} sx={{ fontFamily: f.stack }}>
                          {f.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Höger spalt: i designläge renderar vi BlockCanvas, i direkt-läge ControlledTextEditor. */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {mode === 'design' ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <BlockCanvas
                blocks={blocks}
                selectedBlockId={selectedBlockId}
                docSettings={docSettings}
                onSelectBlock={setSelectedBlockId}
                onInsertBlock={handleInsertBlock}
                onReorderBlock={handleReorderBlock}
                onMoveBlock={handleMoveBlock}
                onDuplicateBlock={handleDuplicateBlock}
                onDeleteBlock={handleDeleteBlock}
              />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
              <ControlledTextEditor name='body' noMargin resizeAllowed rows={40} />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
